import type { ReactNode } from 'react';
import { ResultsOfflineRequest } from '../../shared/results_page/results_offline_request.js';
import {
  useExplorerEnv,
  type FlightHeldOutbound,
  type FlightOfflineContext,
} from './explorer_env.js';
import type { ExplorerState } from './explorer_state.js';
import { flightOfflineProfile } from './flight_offline_profile.js';

/** A IDA já escolhida quando o pedido nasce na fase da VOLTA do ida-e-volta:
 *  a oferta do pick da perna 0, na lista viva ou na semente (o pick do host
 *  nunca está na lista viva — as refs são por sessão). Um voo com conexão tem
 *  N segmentos; a referência guardada é o trecho inteiro (origem do primeiro,
 *  destino e chegada do último). Fora dessa fase, ou sem a oferta em mão,
 *  nada — o pedido segue sem referência em vez de inventar uma. */
export function heldOutboundOf(state: ExplorerState): FlightHeldOutbound | undefined {
  if (state.request.mode !== 'round_trip' || state.activeLeg !== 1) return undefined;
  const offerId = state.chosen[0]?.offerId;
  if (offerId === undefined) return undefined;
  const leg = state.legs[0];
  const live = leg?.phase === 'ready' || leg?.phase === 'seeded' ? leg.offers : [];
  const offer = [...live, ...(state.seeds[0] ?? [])].find((o) => o.offerId === offerId);
  const first = offer?.segments[0];
  const last = offer?.segments[offer.segments.length - 1];
  if (!offer || !first || !last) return undefined;
  return {
    segment: { ...first, to: last.to, arriveAt: last.arriveAt },
    price: offer.price,
    fareClass: offer.fareClass,
  };
}

/** O pé da lista que oferece a busca OFFLINE pela equipe VOLL (o concierge do
 *  legado, `FlightOfflineCard`): só com o port do host (a bancada — a casca do
 *  chat não o passa) e só com a lista da perna aberta ASSENTADA (pronta ou
 *  semeada) — oferecer a saída enquanto a busca corre é desistir por ela. O
 *  explorer congelado (checkout aberto) desliga o confirmar, como o resto. */
export function ExplorerOfflineFoot({
  state,
  frozen,
}: {
  readonly state: ExplorerState;
  readonly frozen: boolean;
}): ReactNode {
  const { copy, locale, offlineRequest: port } = useExplorerEnv();
  const phase = state.legs[state.activeLeg]?.phase;
  if (!port || (phase !== 'ready' && phase !== 'seeded')) return null;
  const profile = flightOfflineProfile(state.request, state.filter, copy, locale);
  return (
    <div className="mt-2">
      <ResultsOfflineRequest
        copy={copy.page.offline}
        disabled={frozen}
        onRequest={() => {
          const held = heldOutboundOf(state);
          const flight: FlightOfflineContext = {
            onlyDirect: state.filter.stops.length === 1 && state.filter.stops[0] === 'nonstop',
            ...(held ? { heldOutbound: held } : {}),
          };
          port.onRequest(profile, flight);
        }}
      />
    </div>
  );
}
